'use client';

import { useEffect, useState } from 'react';
import NavBar from '../home-page/components/NavBar';
import FooterSection from '../home-page/components/FooterSection';
import { generateBreadcrumbSchema } from '@/lib/utils/metadata';
import { legalPageService } from '@/lib/services/legalPageService';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';

const DEFAULT_CONTENT = `
<h2>1. Introduction</h2>
<p>GoGedit Inc. ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website.</p>

<h2>2. Information We Collect</h2>
<p>We collect information that you provide directly to us, including:</p>
<ul>
<li>Personal identification information (name, email address, phone number)</li>
<li>Account credentials</li>
<li>Payment information</li>
<li>Delivery addresses and location data</li>
<li>Vehicle information (for drivers)</li>
<li>Driver's license and insurance information (for drivers)</li>
</ul>

<h2>3. How We Use Your Information</h2>
<p>We use the information we collect to:</p>
<ul>
<li>Provide, maintain, and improve our services</li>
<li>Process transactions and send related information</li>
<li>Send you technical notices and support messages</li>
<li>Respond to your comments and questions</li>
<li>Monitor and analyze trends and usage</li>
<li>Detect, prevent, and address technical issues and fraudulent activity</li>
<li>Provide real-time tracking and location services</li>
</ul>

<h2>4. Location Information</h2>
<p>We collect precise location data from your device when you use our app to enable real-time tracking, match you with nearby drivers, and provide accurate pickup and delivery services. You can disable location services through your device settings, but this may limit functionality.</p>

<h2>5. Information Sharing</h2>
<p>We may share your information with:</p>
<ul>
<li>Drivers and customers to facilitate deliveries</li>
<li>Service providers who perform services on our behalf</li>
<li>Law enforcement when required by law</li>
<li>Other parties with your consent</li>
</ul>
<p>We do not sell your personal information to third parties.</p>

<h2>6. Cookies and Tracking Technologies</h2>
<p>We use cookies and similar tracking technologies to track activity on our service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.</p>

<h2>7. Third-Party Services</h2>
<p>We may employ third-party companies and individuals to facilitate our service, provide the service on our behalf, or assist us in analyzing how our service is used. These third parties have access to your personal data only to perform these tasks on our behalf.</p>

<h2>8. Data Security</h2>
<p>We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure.</p>

<h2>9. Data Retention</h2>
<p>We retain your information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements.</p>

<h2>10. Your Rights</h2>
<p>You have the right to access, correct, delete, and port your personal data. You may also object to or restrict processing and withdraw consent at any time.</p>

<h2>11. Children's Privacy</h2>
<p>Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children.</p>

<h2>12. Changes to This Policy</h2>
<p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.</p>

<h2>13. Contact Us</h2>
<p>If you have questions about this Privacy Policy, please contact us at:<br />
<strong>Email:</strong> info@gogedit.ca<br />
<strong>Location:</strong> Toronto, Ontario, Canada</p>
`;

export default function PrivacyPolicyPage() {
  const [content, setContent] = useState<string>('');
  const [title, setTitle] = useState<string>('Privacy Policy');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: `${SITE_URL}/home-page` },
    { name: 'Privacy Policy', url: `${SITE_URL}/privacy-policy` },
  ]);

  useEffect(() => {
    async function fetchContent() {
      try {
        const page = await legalPageService?.getBySlug('privacy-policy');
        if (page && page?.content) {
          setContent(page?.content);
          setTitle(page?.title);
          setUpdatedAt(page?.updatedAt);
        } else {
          setContent(DEFAULT_CONTENT);
        }
      } catch {
        setContent(DEFAULT_CONTENT);
      } finally {
        setLoading(false);
      }
    }
    fetchContent();
  }, []);

  const formattedDate = updatedAt
    ? new Date(updatedAt)?.toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'March 10, 2026';

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <div className="min-h-screen bg-white">
        <NavBar />

        <div className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-5xl font-bold text-navy-dark mb-4">{title}</h1>

            <p className="text-body-text mb-8 text-sm">
              Last updated: {formattedDate}
            </p>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-10 h-10 border-4 border-brand-purple border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div
                className="prose prose-lg max-w-none prose-headings:text-navy-dark prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-p:text-body-text prose-p:mb-4 prose-li:text-body-text prose-ul:pl-6 prose-ul:space-y-2 prose-ul:mb-4 prose-strong:text-navy-dark"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            )}
          </div>
        </div>

        <FooterSection />
      </div>
    </>
  );
}